import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useState, type ReactElement } from 'react';

const ISO_DATE = 'YYYY-MM-DD';

export type DateRangeValue = {
    from?: string;
    to?: string;
};

export type DateRangeFilterProps = {
    startLabel: string;
    endLabel: string;
    clearLabel: string;
    value: DateRangeValue;
    onChange: (value: DateRangeValue) => void;
};

function toDayjs(value: string | undefined): Dayjs | null {
    return value ? dayjs(value) : null;
}

function DateField({
    label,
    value,
    onChange,
    open,
    onOpen,
    onClose,
    minDate,
    maxDate,
}: {
    label: string;
    value: string | undefined;
    onChange: (value: string | undefined) => void;
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    minDate?: Dayjs | null;
    maxDate?: Dayjs | null;
}): ReactElement {
    return (
        <DatePicker
            label={label}
            value={toDayjs(value)}
            onChange={(date) => onChange(date && date.isValid() ? date.format(ISO_DATE) : undefined)}
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            minDate={minDate ?? undefined}
            maxDate={maxDate ?? undefined}
            format="DD.MM.YYYY"
            slotProps={{
                textField: { size: 'small', fullWidth: true },
                // Match the Figma: calendar icon leads the field.
                inputAdornment: { position: 'start' },
            }}
        />
    );
}

export function DateRangeFilter({
    startLabel,
    endLabel,
    clearLabel,
    value,
    onChange,
}: DateRangeFilterProps): ReactElement {
    const hasValue = value.from !== undefined || value.to !== undefined;
    const [endOpen, setEndOpen] = useState(false);

    return (
        <Stack spacing={2}>
            <DateField
                label={startLabel}
                value={value.from}
                onChange={(from) => {
                    onChange({ ...value, from });
                    if (from && !value.to) {
                        setEndOpen(true);
                    }
                }}
                maxDate={toDayjs(value.to)}
            />
            <DateField
                label={endLabel}
                value={value.to}
                onChange={(to) => onChange({ ...value, to })}
                open={endOpen}
                onOpen={() => setEndOpen(true)}
                onClose={() => setEndOpen(false)}
                minDate={toDayjs(value.from)}
            />
            <Link
                component="button"
                type="button"
                variant="body2"
                underline="always"
                disabled={!hasValue}
                onClick={() => onChange({ from: undefined, to: undefined })}
                sx={{
                    alignSelf: 'flex-start',
                    color: hasValue ? 'text.secondary' : 'text.disabled',
                }}>
                {clearLabel}
            </Link>
        </Stack>
    );
}
